import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { AuthContext } from './AuthContext';
import ValidatedTextField from '../Utilities/Forms/ValidatedTextField';
import LoginViewModel from './ViewModels/LoginViewModel';
import Alert from '@mui/material/Alert';
import ReturnViewModel from '../Contexts/ReturnViewModel';
import { APIContext } from '../Contexts/APIContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import AccessRequestViewModel from './ViewModels/AccessRequestViewModel';

export default function Login() {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const returnURL = params.get('return') != null ? new URL(params.get('return')!) : null;

    const formContext = useForm<LoginViewModel>({
        defaultValues: new LoginViewModel(),
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<ReturnViewModel | null>(null);
    const navigate = useNavigate();
    const { login, authenticated } = useContext(AuthContext);
    const { post, showMessage } = useContext(APIContext);
    const [loginAttempted, setLoginAttempted] = useState<boolean>(false);
    const { setValue, getValues } = formContext;
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!loginAttempted && authenticated) {
            completeLogin();
        }
    }, [authenticated, loginAttempted]);

    const completeLogin = () => {
        if (returnURL != null) {
            navigate(`${returnURL.pathname}${returnURL.search}`);
        }
        else {
            navigate(`/home`);
        }
    }

    const handleLogin = async (payload: LoginViewModel) => {
        setLoginAttempted(true);
        setLoading(true)
        login(payload, (result) => {
            setLoading(false);
            if (result.success) {
                completeLogin();
            }
            else {
                setResponse(result);
                if (result.messages[0] == 'User currently logged in.') {
                    setValue('force', true);
                }
            }
        });
    };

    const contactFormContext = useForm<AccessRequestViewModel>({
        defaultValues: new AccessRequestViewModel(),
    });
    const submiContactForm = async (data: AccessRequestViewModel) => {
        post('Contact', `SendAccessEmailToSupport`, data, (response: any) => {
            showMessage("success", "Success. You will be contacted by a member of TRICS team shortly.");
            setOpen(false);
        });
    };

    return (
        <>
            <FormContainer
                formContext={formContext}
                onSuccess={handleLogin}
            >
                <Grid container>
                    <Grid size={12}>
                        <Typography variant="h4" textAlign="center">Sign in</Typography>
                    </Grid>
                    <Grid size={12}>
                        <ValidatedTextField label="Email" name="email" disabled={loading} />
                    </Grid>
                    <Grid size={12}>
                        <ValidatedTextField label="Password" name="password" type="password" disabled={loading} />
                    </Grid>
                    {response !== null && !response.success &&
                        <Grid size={12}>
                            <Alert severity="error">{response.messages[0]}</Alert>
                        </Grid>
                    }
                    {response !== null && getValues('force') &&
                        <Grid size={12}>
                            <Alert severity="warning">You may log out your existing session below</Alert>
                        </Grid>
                    }
                    <Grid size={12}>
                        <Button type="submit" disabled={loading}>{getValues('force') ? "End existing session and Sign in" : "Sign in"}</Button>
                    </Grid>
                    <Grid size={12}>
                        <Button variant="text" size="small" onClick={() => navigate('/authorisation/forgotpassword')} disabled={loading}>forgotten your password?</Button>
                    </Grid>
                    <Grid size={12}>
                        <Button variant="text" size="small" onClick={() => setOpen(true)} disabled={loading}>TRICS 7 Member without a TRICS 8 login? Click here</Button>
                    </Grid>
                </Grid>
            </FormContainer>
            <Dialog open={open} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant={"h5"}>TRICS 8 Access Request</Typography>
                </DialogTitle>
                <DialogContent>
                    <FormContainer formContext={contactFormContext} onSuccess={submiContactForm}>
                        <Grid container>
                            <Grid size={12}>
                                <Typography variant="body1">Name</Typography>
                                <ValidatedTextField name={'name'} required />
                            </Grid>
                            <Grid size={12}>
                                <Typography variant="body1">Email</Typography>
                                <ValidatedTextField name={'email'} required />
                            </Grid>
                            <Grid size={12}>
                                <Typography variant="body1">Legacy licence number</Typography>
                                <Typography variant="caption">This is the 6 digit number you used to use to login to TRICS 7</Typography>
                                <ValidatedTextField name={'licence'} required />
                            </Grid>
                            <Grid size={6}>
                                <Stack direction="row" spacing={1}>
                                    <Button type="submit">Submit</Button>
                                    <Button type="reset" onClick={() => setOpen(false)}>Cancel</Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </FormContainer>
                </DialogContent>
            </Dialog>
        </>
    );
}
